import * as React from 'react'

import {
  Alert,
  Box,
  Icon,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { FormikProvider } from 'formik'

import Sidepanel from 'modules/core/components/Sidepanel'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EvaluationFormEditor } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import {
  buildEmptyQuestion,
  useEvaluationFormForm,
} from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

import { useUpdateEvaluationFormTemplate } from './useUpdateEvaluationFormTemplate'

interface Props {
  onClose: () => void
  evaluationFormId: number
}

export const EditEvaluationFormTemplateSidepanel = ({ onClose, evaluationFormId }: Props) => {
  const { openDialog } = useConfirmationDialog()
  const { updateForm } = useUpdateEvaluationFormTemplate()

  const { form, hasInvalidQuestions } = useEvaluationFormForm({
    defaultValues: {
      name: '',
      questions: [buildEmptyQuestion()],
    },
    async onSubmit(data, { setErrors }) {
      const questions = data.questions.map((question) => ({
        id: question.id,
        description: question.description,
        text: question.text,
      }))
      const result = await updateForm({
        id: evaluationFormId,
        name: data.name,
        questions,
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.edit.success_message'),
        })
        onClose()
      }

      return handled
    },
  })

  const onCancelClicked = () => {
    if (form.dirty) {
      openDialog({
        illustration: 'alert',
        title: i18n.t('ats.evaluation_forms.form.unsaved_changes.title'),
        content: i18n.t('ats.evaluation_forms.form.unsaved_changes.description'),
        confirmLabel: i18n.t('common.cancel'),
        cancelLabel: i18n.t('ats.evaluation_forms.form.unsaved_changes.continue'),
        onConfirm: onClose,
      })
    } else {
      onClose()
    }
  }

  return (
    <FormikProvider value={form}>
      <Sidepanel
        open
        width="wide"
        title={i18n.t('ats.evaluation_forms.edit.title')}
        footer={
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="center">
            {form.dirty ? (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-circle-line" color="grey800" />
                <Text.Normal size="200">{i18n.t('ats.evaluation_forms.form.warning')}</Text.Normal>
              </Box>
            ) : (
              <></>
            )}

            <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
              <F0Button
                onClick={onCancelClicked}
                variant="outline"
                label={i18n.t('common.cancel')}
              />
              <F0Button
                onClick={() => form.handleSubmit()}
                disabled={form.isSubmitting}
                label={i18n.t('common.save')}
              />
            </Box>
          </Box>
        }
        onClose={onClose}
        closeLabel={i18n.t('common.close')}
      >
        <Box gap="s16">
          <Alert>{i18n.t('ats.evaluation_forms.edit.info')}</Alert>
          <EvaluationFormEditor
            evaluationFormId={evaluationFormId}
            hasInvalidQuestions={hasInvalidQuestions}
          />
        </Box>
      </Sidepanel>
    </FormikProvider>
  )
}
